import React, { useEffect, useRef, useState, Suspense, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
  Environment,
  ContactShadows,
  Float,
  PerspectiveCamera,
  useTexture,
  Text3D,
  Center,
  MeshTransmissionMaterial,
  RoundedBox
} from '@react-three/drei';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import CardDesignerV2 from './components/CardDesignerV2';
import NFCCard3D from './components/NFCCard3D';
import { useToast } from './hooks/useToast';
import { Toast } from './components/Toast';
import { useFormValidation, validators } from './hooks/useFormValidation';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import useSimpleCartStore from './store/simpleCartStore';
import SimpleCart from './components/SimpleCart';
import { theme, getTheme } from './theme';
import { useSubdomain } from './hooks/useSubdomain';

// ============================================================================
// cloudcard - Smart NFC Business Cards
// Compatible with: React, Vite, Kimono UIKit, cloudcard theming system
// Architecture: Serverless-first (Cloudflare Workers compatible)
// ============================================================================

// ============================================================================
// SCROLL-ROTATING CARD COMPONENT (Apple-style)
// ============================================================================

function RotatingCardGroup({ scrollProgress }) {
  const groupRef = useRef();

  useFrame(() => {
    if (groupRef.current) {
      // Apple-style scroll animation phases:
      // Phase 1 (0.12-0.22): Card appears and starts rotating
      // Phase 2 (0.22-0.40): Dramatic rotation and flip
      // Phase 3 (0.40-0.55): Settles into final position

      if (scrollProgress >= 0.12 && scrollProgress <= 0.60) {
        const phase1 = Math.min(Math.max((scrollProgress - 0.12) / 0.10, 0), 1);
        const phase2 = Math.min(Math.max((scrollProgress - 0.22) / 0.18, 0), 1);
        const phase3 = Math.min(Math.max((scrollProgress - 0.40) / 0.15, 0), 1);

        // Y-axis: Dramatic 360° + 180° rotation (1.5 full rotations)
        const rotY =
          phase1 * Math.PI * 0.3 +  // Initial tilt
          phase2 * Math.PI * 3 +     // Main dramatic rotation (1.5 full spins)
          phase3 * Math.PI * 0.2;    // Settle

        // X-axis: Tilt up, then down, then level
        const rotX =
          Math.sin(phase1 * Math.PI) * 0.4 +          // Rise
          Math.sin(phase2 * Math.PI) * 0.6 -          // Dramatic tilt
          phase3 * 0.3;                                // Settle to level

        // Z-axis: Subtle roll for more dynamics
        const rotZ = Math.sin(phase2 * Math.PI * 2) * 0.15;

        // Smooth interpolation (Apple uses very smooth easing)
        groupRef.current.rotation.y += (rotY - groupRef.current.rotation.y) * 0.12;
        groupRef.current.rotation.x += (rotX - groupRef.current.rotation.x) * 0.12;
        groupRef.current.rotation.z += (rotZ - groupRef.current.rotation.z) * 0.12;

        // Scale effect: slightly smaller during rotation, then back to normal
        const scale = 1 - (phase2 * 0.15) + (phase3 * 0.15);
        groupRef.current.scale.setScalar(scale);
      }
    }
  });

  return (
    <group ref={groupRef}>
      <NFCCard3D
        color="#ffffff"
        hovered={scrollProgress > 0.15 && scrollProgress < 0.60}
        rotation={[0, 0, 0]}
        autoRotate={false}
      />
    </group>
  );
}

function ScrollRotatingCard({ scrollProgress, theme }) {
  return (
    <Canvas
      shadows
      dpr={[1, 1.5]}
      camera={{ position: [0, 0, 8], fov: 45 }}
      gl={{ antialias: true, powerPreference: 'high-performance' }}
      performance={{ min: 0.5 }}
      style={{ width: '100%', height: '100%' }}
    >
      <Suspense fallback={null}>
        <ambientLight intensity={0.5} />
        <spotLight position={[10, 10, 10]} angle={0.3} penumbra={1} intensity={1.2} castShadow shadow-mapSize={512} />
        <spotLight position={[-10, -10, -10]} angle={0.3} penumbra={1} intensity={0.5} color={theme.colors.accent} />

        <RotatingCardGroup scrollProgress={scrollProgress} />

        <ContactShadows
          position={[0, -2.5, 0]}
          opacity={0.5}
          scale={12}
          blur={2.5}
          far={4}
        />

        <Environment preset="city" />
      </Suspense>
    </Canvas>
  );
}

// ============================================================================
// 3D NFC CARD COMPONENT
// ============================================================================

function NFCCard({ rotation = [0, 0, 0], position = [0, 0, 0], scale = 1, color = '#00ff88', hovered = false }) {
  const meshRef = useRef();
  const glowRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = rotation[1] + Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
      meshRef.current.rotation.x = rotation[0] + Math.cos(state.clock.elapsedTime * 0.3) * 0.05;
    }
    if (glowRef.current) {
      glowRef.current.intensity = hovered ? 2 : 0.5 + Math.sin(state.clock.elapsedTime * 2) * 0.3;
    }
  });

  return (
    <group position={position} scale={scale}>
      <Float speed={2} rotationIntensity={0.2} floatIntensity={0.5}>
        <mesh ref={meshRef} castShadow receiveShadow>
          <RoundedBox args={[3.4, 2.1, 0.08]} radius={0.08} smoothness={4}>
            <meshPhysicalMaterial
              color={theme.colors.surface}
              metalness={0.9}
              roughness={0.1}
              clearcoat={1}
              clearcoatRoughness={0.1}
              envMapIntensity={1.5}
            />
          </RoundedBox>
          
          {/* NFC chip detail */}
          <mesh position={[-0.8, 0.4, 0.05]}>
            <boxGeometry args={[0.5, 0.4, 0.02]} />
            <meshStandardMaterial color="#3f3f46" metalness={0.8} roughness={0.2} />
          </mesh>
          
          {/* NFC antenna rings */}
          {[0.3, 0.4, 0.5].map((r, i) => (
            <mesh key={i} position={[0.8, 0, 0.045]} rotation={[0, 0, 0]}>
              <torusGeometry args={[r, 0.015, 8, 32]} />
              <meshStandardMaterial 
                color={color} 
                emissive={color}
                emissiveIntensity={hovered ? 1 : 0.3}
                metalness={0.5}
                roughness={0.3}
              />
            </mesh>
          ))}
          
          {/* Accent line */}
          <mesh position={[0, -0.7, 0.045]}>
            <boxGeometry args={[2.8, 0.03, 0.01]} />
            <meshStandardMaterial 
              color={color}
              emissive={color}
              emissiveIntensity={hovered ? 2 : 0.5}
            />
          </mesh>
        </mesh>
        
        {/* Glow effect */}
        <pointLight 
          ref={glowRef}
          color={color} 
          intensity={0.5} 
          distance={3}
          position={[0, 0, 0.5]}
        />
      </Float>
    </group>
  );
}

// ============================================================================
// HERO 3D SCENE
// ============================================================================

function HeroScene({ scrollProgress }) {
  const groupRef = useRef();
  const { viewport } = useThree();
  
  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.rotation.y = scrollProgress * Math.PI * 2;
      groupRef.current.position.y = -scrollProgress * 2;
      groupRef.current.scale.setScalar(1 - scrollProgress * 0.3);
    }
  });

  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 0, 8]} fov={45} />
      <ambientLight intensity={0.2} />
      <directionalLight position={[5, 5, 5]} intensity={1} castShadow />
      <spotLight position={[-5, 5, 5]} intensity={0.5} color="#00d4ff" />
      
      <group ref={groupRef}>
        <NFCCard3D
          color={theme.colors.surface}
          hovered={scrollProgress < 0.1}
          rotation={[0.2, 0, 0.1]}
          autoRotate={true}
        />
      </group>
      
      <ContactShadows 
        position={[0, -2, 0]} 
        opacity={0.4} 
        scale={10} 
        blur={2.5} 
        far={4}
      />
      <Environment preset="city" />
    </>
  );
}

// ============================================================================
// PRODUCT CARD WITH HOVER ANIMATIONS
// ============================================================================

function ProductCard({ product, index, theme }) {
  const [hovered, setHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const cardRef = useRef();
  const { addToCart } = useSimpleCartStore();

  // Simulate 3D loading (in real app, this would be based on actual 3D model loading)
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleAddToCart = () => {
    addToCart({
      design: { name: product.name, color: product.color },
      product: product,
      price: product.price * 100, // Convert to cents
      quantity: 1,
    });
  };

  return (
    <motion.div
      ref={cardRef}
      className="product-card"
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: theme.colors.surface,
        borderRadius: '20px',
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        border: `2px solid ${hovered ? theme.colors.accent : theme.colors.border}`,
        boxShadow: hovered ? '0 12px 40px rgba(14, 165, 233, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.3s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
      }}
    >
      {/* 3D Card Preview */}
      <div style={{ height: '280px', position: 'relative', width: '100%', overflow: 'hidden' }}>
        {isLoading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            zIndex: 10,
            background: theme.colors.surface,
          }}>
            <LoadingSkeleton width="85%" height="180px" borderRadius="16px" />
          </div>
        )}
        <Canvas
          shadows
          dpr={[1, 1.5]}
          gl={{ antialias: true, powerPreference: 'high-performance' }}
          performance={{ min: 0.5 }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.3} />
            <directionalLight position={[3, 3, 3]} intensity={0.8} castShadow shadow-mapSize={512} />
            <spotLight position={[-3, 3, 3]} intensity={0.4} color="#00d4ff" />

            <NFCCard3D
              color={product.color}
              hovered={hovered}
              rotation={[0.3, hovered ? 0.5 : 0, 0]}
              autoRotate={false}
            />

            <ContactShadows
              position={[0, -1.2, 0]}
              opacity={0.3}
              scale={5}
              blur={2}
            />
            <Environment preset="city" />
          </Suspense>
        </Canvas>
        
        {/* Hover badge */}
        <AnimatePresence>
          {hovered && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 10 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: theme.colors.gradient,
                padding: '8px 16px',
                borderRadius: '100px',
                fontSize: '12px',
                fontWeight: '600',
                fontFamily: theme.fonts.mono,
                color: theme.colors.bg,
              }}
            >
              TAP TO CONFIGURE
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Product Info */}
      <div style={{ padding: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '12px'
        }}>
          <h3 style={{
            fontFamily: theme.fonts.display,
            fontSize: '22px',
            fontWeight: '600',
            color: theme.colors.text,
            margin: 0,
          }}>
            {product.name}
          </h3>
          <span style={{
            fontFamily: theme.fonts.display,
            fontSize: '24px',
            fontWeight: '700',
            color: theme.colors.text,
          }}>
            ${product.price}
          </span>
        </div>
        
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textMuted,
          margin: '0 0 16px 0',
          lineHeight: '1.5',
        }}>
          {product.description}
        </p>

        {/* Tags */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {product.tags.map((tag, i) => (
            <span
              key={i}
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontFamily: theme.fonts.body,
                fontWeight: '500',
                background: `${theme.colors.accent}10`,
                color: theme.colors.accent,
                border: `1px solid ${theme.colors.accent}25`,
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Add to Cart Button */}
        <motion.button
          onClick={handleAddToCart}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            width: '100%',
            marginTop: '4px',
            padding: '14px',
            background: hovered ? theme.colors.gradient : 'transparent',
            border: hovered ? 'none' : `1px solid ${theme.colors.border}`,
            borderRadius: '12px',
            cursor: 'pointer',
            fontFamily: theme.fonts.body,
            fontSize: '15px',
            fontWeight: '600',
            color: hovered ? 'white' : theme.colors.text,
            transition: 'all 0.3s ease',
          }}
        >
          {hovered ? 'Add to Cart' : 'View Details'}
        </motion.button>
      </div>
    </motion.div>
  );
}

// ============================================================================
// NAVIGATION
// ============================================================================

function Navigation({ currentPage, setCurrentPage, theme }) {
  const [scrolled, setScrolled] = useState(false);
  const { getItemCount } = useSimpleCartStore();
  const itemCount = getItemCount();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'shop', label: 'Shop' },
    { id: 'design', label: 'Design', highlight: true }, // Card Designer - NEW!
    { id: 'about', label: 'About' },
    { id: 'support', label: 'Support' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: '100%',
        maxWidth: '100vw',
        padding: 'clamp(12px, 3vw, 20px) clamp(16px, 4vw, 40px)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: scrolled ? `${theme.colors.bg}ee` : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${theme.colors.border}` : 'none',
        transition: 'all 0.3s ease',
        gap: 'clamp(8px, 2vw, 16px)',
        flexWrap: 'wrap',
        overflowX: 'hidden',
      }}
    >
      {/* Logo */}
      <motion.div
        whileHover={{ scale: 1.05 }}
        style={{
          fontFamily: theme.fonts.display,
          fontSize: '24px',
          fontWeight: '800',
          background: theme.colors.gradient,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
        onClick={() => setCurrentPage('home')}
      >
        cloudcard
      </motion.div>

      {/* Nav Items */}
      <div style={{ display: 'flex', gap: 'clamp(4px, 1vw, 8px)', flexWrap: 'wrap', justifyContent: 'center' }}>
        {navItems.map((item) => (
          <motion.button
            key={item.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setCurrentPage(item.id)}
            style={{
              padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 20px)',
              background: item.highlight
                ? (currentPage === item.id ? theme.colors.accent : `${theme.colors.accent}33`)
                : (currentPage === item.id ? theme.colors.surface : 'transparent'),
              border: item.highlight ? `1px solid ${theme.colors.accent}` : 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: theme.fonts.body,
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              fontWeight: item.highlight ? '600' : '500',
              color: item.highlight
                ? (currentPage === item.id ? theme.colors.bg : theme.colors.accent)
                : (currentPage === item.id ? theme.colors.accent : theme.colors.textMuted),
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </motion.button>
        ))}
      </div>

      {/* Cart */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setCurrentPage('cart')}
        aria-label={`Shopping cart with ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
        style={{
          padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 24px)',
          background: theme.colors.gradient,
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontFamily: theme.fonts.display,
          fontSize: 'clamp(12px, 2.5vw, 14px)',
          fontWeight: '600',
          color: theme.colors.bg,
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(4px, 1vw, 8px)',
          whiteSpace: 'nowrap',
          position: 'relative',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
          <line x1="3" y1="6" x2="21" y2="6"/>
          <path d="M16 10a4 4 0 0 1-8 0"/>
        </svg>
        Cart {itemCount > 0 && `(${itemCount})`}
        {itemCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: '#ef4444',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '700',
            }}
          >
            {itemCount}
          </motion.span>
        )}
      </motion.button>
    </motion.nav>
  );
}

// ============================================================================
// HOME / LANDING PAGE
// ============================================================================

function HomePage({ setCurrentPage, theme }) {
  const containerRef = useRef();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  const [scrollProgress, setScrollProgress] = useState(0);
  
  useEffect(() => {
    return scrollYProgress.onChange((v) => setScrollProgress(v));
  }, [scrollYProgress]);

  const heroOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.25], [1, 0.95]);

  return (
    <div ref={containerRef} style={{ minHeight: '200vh', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <motion.section
        style={{
          height: '100vh',
          width: '100%',
          maxWidth: '100vw',
          position: 'sticky',
          top: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: heroOpacity,
          scale: heroScale,
          overflowX: 'hidden',
        }}
      >
        {/* 3D Canvas Background - subtle depth, lazy loaded */}
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          opacity: 0.15,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          pointerEvents: 'none',
        }}>
          <Canvas
            shadows
            dpr={[1, 1.5]}
            gl={{ antialias: false, powerPreference: 'high-performance' }}
            performance={{ min: 0.5 }}
          >
            <Suspense fallback={null}>
              <HeroScene scrollProgress={scrollProgress} />
            </Suspense>
          </Canvas>
        </div>

        {/* Hero Content */}
        <div style={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          padding: 'clamp(40px, 8vh, 80px) clamp(20px, 4vw, 40px)',
          maxWidth: '1100px',
          width: '100%',
        }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <span style={{
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              letterSpacing: '1px',
              color: theme.colors.accent,
              display: 'block',
              marginBottom: '24px',
              fontWeight: '600',
            }}>
              Smart NFC Business Cards
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            style={{
              fontFamily: theme.fonts.display,
              fontSize: 'clamp(36px, 8vw, 96px)',
              fontWeight: '800',
              color: theme.colors.text,
              margin: '0 0 24px 0',
              lineHeight: '1.1',
              letterSpacing: 'clamp(-2px, -0.5vw, -1px)',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              hyphens: 'auto',
            }}
          >
            TAP. CONNECT.
            <br />
            <span style={{
              background: theme.colors.gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              display: 'inline-block',
              boxDecorationBreak: 'clone',
              WebkitBoxDecorationBreak: 'clone',
            }}>
              SHARE WITH EASE.*
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            style={{
              fontFamily: theme.fonts.body,
              fontSize: 'clamp(15px, 4vw, 18px)',
              color: theme.colors.textMuted,
              margin: '0 0 40px 0',
              maxWidth: '600px',
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: '1.6',
              padding: '0 10px',
            }}
          >
            NFC business cards with cloud-powered analytics. Track unique visitors,
            measure engagement, and understand your reach—all in one tap.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '0 10px',
            }}
          >
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: '0 8px 30px rgba(14, 165, 233, 0.3)' }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrentPage('design')}
              style={{
                padding: 'clamp(16px, 3vw, 18px) clamp(32px, 6vw, 48px)',
                background: theme.colors.gradient,
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontFamily: theme.fonts.body,
                fontSize: 'clamp(16px, 3.5vw, 18px)',
                fontWeight: '600',
                color: 'white',
                boxShadow: '0 4px 20px rgba(14, 165, 233, 0.2)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              Design Your Card
            </motion.button>
            <motion.button
              whileHover={{ color: theme.colors.accent }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentPage('shop')}
              style={{
                padding: 'clamp(16px, 3vw, 18px) clamp(20px, 5vw, 24px)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: theme.fonts.body,
                fontSize: 'clamp(16px, 3.5vw, 18px)',
                fontWeight: '500',
                color: theme.colors.textMuted,
                transition: 'color 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              Shop Now →
            </motion.button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          style={{
            position: 'absolute',
            bottom: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{
            fontFamily: theme.fonts.mono,
            fontSize: '10px',
            letterSpacing: '2px',
            color: theme.colors.textMuted,
          }}>
            SCROLL TO EXPLORE
          </span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            style={{
              width: '24px',
              height: '40px',
              border: `2px solid ${theme.colors.border}`,
              borderRadius: '12px',
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '8px',
            }}
          >
            <motion.div
              style={{
                width: '4px',
                height: '8px',
                background: theme.colors.accent,
                borderRadius: '2px',
              }}
            />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Product Showcase Section - Whitespace with Rotating 3D Card */}
      <section style={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        padding: 'clamp(100px, 20vh, 200px) clamp(20px, 5vw, 40px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflowX: 'hidden',
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          style={{
            width: 'min(1000px, 95vw)',
            height: 'clamp(400px, 65vh, 650px)',
            maxWidth: '100%',
            position: 'relative',
          }}
        >
          {/* Multi-layer background glow */}
          <div style={{
            position: 'absolute',
            inset: '-40%',
            background: `radial-gradient(circle, ${theme.colors.accent}25 0%, ${theme.colors.accent}08 40%, transparent 70%)`,
            filter: 'blur(120px)',
            zIndex: -1,
          }} />

          {/* Secondary glow pulse */}
          <div style={{
            position: 'absolute',
            inset: '-20%',
            background: `radial-gradient(circle, ${theme.colors.accent}15 0%, transparent 60%)`,
            filter: 'blur(80px)',
            zIndex: -1,
          }} />

          {/* Label Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
            }}
          >
            <div style={{
              padding: '12px 32px',
              background: `${theme.colors.surface}f5`,
              backdropFilter: 'blur(20px)',
              borderRadius: '100px',
              border: `1px solid ${theme.colors.border}`,
              fontFamily: theme.fonts.mono,
              fontSize: '11px',
              fontWeight: '600',
              color: theme.colors.text,
              textTransform: 'uppercase',
              letterSpacing: '2px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}>
              Premium Design
            </div>
          </motion.div>

          {/* 3D Rotating Card */}
          <Canvas
            shadows
            dpr={[1, 1.5]}
            camera={{ position: [0, 0, 8], fov: 45 }}
            gl={{ antialias: true, powerPreference: 'high-performance' }}
            performance={{ min: 0.5 }}
            style={{ width: '100%', height: '100%' }}
          >
            <Suspense fallback={null}>
              <ambientLight intensity={0.5} />
              <spotLight position={[10, 10, 10]} angle={0.3} penumbra={1} intensity={1.2} castShadow shadow-mapSize={512} />
              <spotLight position={[-10, -10, -10]} angle={0.3} penumbra={1} intensity={0.5} color={theme.colors.accent} />

              <Float speed={1.5} rotationIntensity={0.4} floatIntensity={0.6}>
                <NFCCard3D
                  color="#ffffff"
                  hovered={true}
                  rotation={[0.2, 0, 0.1]}
                  autoRotate={true}
                />
              </Float>

              <ContactShadows
                position={[0, -2.5, 0]}
                opacity={0.5}
                scale={12}
                blur={2.5}
                far={4}
              />

              <Environment preset="city" />
            </Suspense>
          </Canvas>

          {/* Bottom Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
            style={{
              position: 'absolute',
              bottom: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              textAlign: 'center',
            }}
          >
            <div style={{
              fontFamily: theme.fonts.display,
              fontSize: 'clamp(24px, 4vw, 36px)',
              fontWeight: '700',
              color: theme.colors.text,
              textShadow: `0 2px 30px ${theme.colors.bg}`,
            }}>
              Built to Impress
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section style={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        padding: 'clamp(80px, 15vw, 160px) clamp(20px, 5vw, 40px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'hidden',
      }}>
        <div style={{ maxWidth: '1200px', width: '100%' }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center', marginBottom: 'clamp(60px, 10vh, 100px)' }}
          >
            <h2 style={{
              fontFamily: theme.fonts.display,
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: '700',
              color: theme.colors.text,
              margin: '0 0 24px 0',
            }}>
              Engineered for{' '}
              <span style={{
                background: theme.colors.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}>
                Impact
              </span>
            </h2>
            <p style={{
              fontFamily: theme.fonts.body,
              fontSize: '18px',
              color: theme.colors.textMuted,
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6',
            }}>
              Each card uses NFC-216 chips and durable materials built to last years of daily use.
            </p>
          </motion.div>

          {/* Core Features - Top Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: 'clamp(20px, 4vw, 32px)',
            marginBottom: 'clamp(24px, 4vw, 32px)',
            width: '100%',
            maxWidth: '100%',
          }}>
            {[
              {
                title: 'Built to Last',
                description: 'Metal and PVC composite construction that holds up to daily wear.',
              },
              {
                title: 'Works Everywhere',
                description: 'Compatible with iOS, Android, and all NFC devices.',
              },
              {
                title: 'Fully Customizable',
                description: 'Upload your own design or choose from our templates.',
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                whileHover={{
                  y: -8,
                  boxShadow: '0 12px 40px rgba(14, 165, 233, 0.15)',
                }}
                style={{
                  background: theme.colors.surface,
                  padding: 'clamp(32px, 6vw, 48px) clamp(24px, 5vw, 32px)',
                  borderRadius: 'clamp(16px, 3vw, 20px)',
                  border: `1px solid ${theme.colors.border}`,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                }}
              >
                <h3 style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 'clamp(18px, 3.5vw, 20px)',
                  fontWeight: '600',
                  color: theme.colors.text,
                  margin: '0 0 12px 0',
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  fontFamily: theme.fonts.body,
                  fontSize: 'clamp(14px, 2.5vw, 15px)',
                  color: theme.colors.textMuted,
                  margin: 0,
                  lineHeight: '1.6',
                }}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Cloud Analytics - Centered Featured Card */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
          }}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{
                y: -8,
                boxShadow: '0 16px 50px rgba(14, 165, 233, 0.2)',
              }}
              style={{
                background: `linear-gradient(135deg, ${theme.colors.surface} 0%, ${theme.colors.bgAlt} 100%)`,
                padding: '56px 48px',
                borderRadius: '24px',
                border: `2px solid ${theme.colors.accent}25`,
                boxShadow: '0 8px 24px rgba(14, 165, 233, 0.08)',
                transition: 'all 0.3s ease',
                maxWidth: '700px',
                width: '100%',
                position: 'relative',
              }}
            >
              {/* Optional Badge */}
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '11px',
                fontFamily: theme.fonts.mono,
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                background: `${theme.colors.accent}15`,
                color: theme.colors.accent,
                border: `1px solid ${theme.colors.accent}30`,
              }}>
                Optional
              </div>

              <h3 style={{
                fontFamily: theme.fonts.display,
                fontSize: '24px',
                fontWeight: '700',
                color: theme.colors.text,
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <span style={{
                  background: theme.colors.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Cloud Analytics
                </span>
              </h3>
              <p style={{
                fontFamily: theme.fonts.body,
                fontSize: '16px',
                color: theme.colors.textMuted,
                margin: 0,
                lineHeight: '1.7',
              }}>
                Track engagement, measure reach, and understand your connections.
                Get detailed insights on unique visitors, tap locations, and conversion
                metrics—all in one powerful dashboard.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Display Section - Premium PDP Style */}
      <section style={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        padding: 'clamp(80px, 15vw, 160px) clamp(20px, 5vw, 40px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(180deg, ${theme.colors.bg} 0%, ${theme.colors.bgAlt} 50%, ${theme.colors.bg} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          maxWidth: '1400px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
          gap: 'clamp(32px, 6vw, 64px)',
          alignItems: 'center',
        }}>
          {/* Left: 3D Card Display */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'relative',
              height: 'clamp(350px, 50vw, 550px)',
              width: '100%',
            }}
          >
            {/* Background glow */}
            <div style={{
              position: 'absolute',
              inset: '-10%',
              background: `radial-gradient(circle, ${theme.colors.accent}20 0%, transparent 70%)`,
              filter: 'blur(80px)',
              zIndex: 0,
            }} />

            {/* 3D Card Canvas - Reduced NFC waves */}
            <div style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
            }}>
              <Canvas
                shadows
                dpr={[1, 1.5]}
                camera={{ position: [0, 0, 8], fov: 45 }}
                gl={{ antialias: true, powerPreference: 'high-performance' }}
                performance={{ min: 0.5 }}
              >
                <Suspense fallback={null}>
                  <ambientLight intensity={0.5} />
                  <spotLight position={[10, 10, 10]} angle={0.3} penumbra={1} intensity={1.2} castShadow shadow-mapSize={512} />
                  <spotLight position={[-10, -10, -10]} angle={0.3} penumbra={1} intensity={0.5} color={theme.colors.accent} />

                  <Float speed={1.2} rotationIntensity={0.4} floatIntensity={0.3}>
                    <NFCCard3D
                      color={theme.colors.surface}
                      hovered={false}
                      rotation={[Math.PI * 0.15, Math.PI * 0.2, Math.PI * 0.05]}
                      autoRotate={true}
                    />
                  </Float>

                  <ContactShadows
                    position={[0, -2.5, 0]}
                    opacity={0.5}
                    scale={12}
                    blur={2.5}
                    far={4}
                  />

                  <Environment preset="city" />
                </Suspense>
              </Canvas>
            </div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              style={{
                position: 'absolute',
                top: '10%',
                right: '10%',
                zIndex: 10,
                padding: '12px 24px',
                background: theme.colors.gradient,
                borderRadius: '100px',
                fontFamily: theme.fonts.mono,
                fontSize: '12px',
                fontWeight: '600',
                color: 'white',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: theme.shadows.accentHover,
              }}
            >
              PREMIUM
            </motion.div>

            {/* Design Customization Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              style={{
                position: 'absolute',
                bottom: '10%',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                padding: '16px 28px',
                background: `${theme.colors.surface}cc`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.colors.accent}40`,
                borderRadius: '16px',
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                fontWeight: '500',
                color: theme.colors.text,
                textAlign: 'center',
                boxShadow: `0 8px 24px ${theme.colors.bg}60`,
              }}
            >
              <div style={{
                color: theme.colors.accent,
                fontWeight: '600',
                marginBottom: '4px',
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}>
                Your Design Here
              </div>
              <div style={{
                fontSize: '12px',
                color: theme.colors.textMuted,
              }}>
                Fully customizable card surface
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(20px, 4vw, 32px)',
            }}
          >
            {/* Product Title */}
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                style={{
                  fontFamily: theme.fonts.mono,
                  fontSize: '12px',
                  letterSpacing: '2px',
                  color: theme.colors.accent,
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  display: 'block',
                  marginBottom: '16px',
                }}
              >
                Flagship Edition
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 'clamp(36px, 6vw, 56px)',
                  fontWeight: '800',
                  color: theme.colors.text,
                  margin: '0 0 16px 0',
                  lineHeight: '1.1',
                  letterSpacing: 'clamp(-2px, -0.5vw, -1px)',
                }}
              >
                CloudCard
                <br />
                <span style={{
                  background: theme.colors.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Voyageur
                </span>
              </motion.h2>
            </div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              style={{
                fontFamily: theme.fonts.body,
                fontSize: 'clamp(16px, 3vw, 18px)',
                color: theme.colors.textMuted,
                lineHeight: '1.7',
                margin: 0,
              }}
            >
              A durable plastic card with embedded NFC chip and antenna technology.
              Customize your design, track every tap with advanced analytics, and share
              your digital presence with a single touch.
            </motion.p>

            {/* Key Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {[
                { title: 'Custom Design', desc: 'Design your own card with our easy-to-use editor' },
                { title: 'NFC Technology', desc: 'Embedded chip & antenna inside durable plastic card' },
                { title: 'Advanced Analytics', desc: 'Real-time engagement tracking' },
                { title: 'Instant Sharing', desc: 'One tap to share everything' },
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.7 + i * 0.1 }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    padding: '16px',
                    background: `${theme.colors.surface}`,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                  }}
                  whileHover={{
                    borderColor: theme.colors.accent,
                    boxShadow: `0 4px 12px ${theme.colors.accent}15`,
                  }}
                >
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: theme.colors.gradient,
                    flexShrink: 0,
                    marginTop: '6px',
                  }} />
                  <div>
                    <div style={{
                      fontFamily: theme.fonts.display,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: theme.colors.text,
                      marginBottom: '4px',
                    }}>
                      {feature.title}
                    </div>
                    <div style={{
                      fontFamily: theme.fonts.body,
                      fontSize: '14px',
                      color: theme.colors.textMuted,
                      lineHeight: '1.5',
                    }}>
                      {feature.desc}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Price and CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 1 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                paddingTop: '8px',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '12px',
              }}>
                <span style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 'clamp(32px, 5vw, 42px)',
                  fontWeight: '700',
                  color: theme.colors.text,
                }}>
                  $39
                </span>
                <span style={{
                  fontFamily: theme.fonts.body,
                  fontSize: 'clamp(14px, 3vw, 16px)',
                  color: theme.colors.textMuted,
                  textDecoration: 'line-through',
                }}>
                  $49
                </span>
              </div>

              <motion.button
                whileHover={{ scale: 1.02, boxShadow: theme.shadows.accentHover }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setCurrentPage('shop')}
                style={{
                  padding: 'clamp(16px, 3vw, 20px) clamp(32px, 8vw, 48px)',
                  background: theme.colors.gradient,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontFamily: theme.fonts.body,
                  fontSize: 'clamp(16px, 3vw, 18px)',
                  fontWeight: '600',
                  color: 'white',
                  boxShadow: theme.shadows.accent,
                  transition: 'all 0.2s ease',
                  width: '100%',
                  maxWidth: '400px',
                }}
              >
                Get Started
              </motion.button>

              <p style={{
                fontFamily: theme.fonts.body,
                fontSize: '13px',
                color: theme.colors.textMuted,
                margin: 0,
              }}>
                Ships free • 30-day returns • Lifetime warranty
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// SHOP PAGE
// ============================================================================

const products = [
  {
    id: 1,
    name: 'CloudCard Voyageur',
    price: 29,
    description: 'Our flagship card with cloud-powered analytics. Track every tap.',
    color: theme.colors.accent,
    tags: ['BESTSELLER', 'ANALYTICS'],
  },
  {
    id: 2,
    name: 'cloudcard Premium',
    price: 39,
    description: 'Premium materials with advanced telemetry features.',
    color: '#3b82f6',
    tags: ['PREMIUM', 'TELEMETRY'],
  },
  {
    id: 3,
    name: 'cloudcard Minimal',
    price: 34,
    description: 'Clean design with essential NFC features. Simple and effective.',
    color: '#06b6d4',
    tags: ['NEW', 'MINIMAL'],
  },
  {
    id: 4,
    name: 'cloudcard Custom',
    price: 49,
    description: 'Full customization with branded analytics dashboard.',
    color: '#8b5cf6',
    tags: ['CUSTOM', 'BRANDED'],
  },
  {
    id: 5,
    name: 'cloudcard Eco',
    price: 44,
    description: 'Sustainable materials with full cloud integration.',
    color: '#10b981',
    tags: ['ECO', 'SUSTAINABLE'],
  },
  {
    id: 6,
    name: 'cloudcard Team',
    price: 199,
    description: '10 cards with unified team dashboard and shared analytics.',
    color: '#f59e0b',
    tags: ['BULK', 'TEAM'],
  },
];

function ShopPage({ theme }) {
  const { filter: subdomainFilter, subdomain, isSubdomainSite } = useSubdomain();
  const [filter, setFilter] = useState('all');

  // Auto-set filter based on subdomain on mount
  useEffect(() => {
    if (subdomainFilter && filter === 'all') {
      setFilter(subdomainFilter);
      console.log(`[ShopPage] Auto-filtering to "${subdomainFilter}" based on subdomain`);
    }
  }, [subdomainFilter, filter]);

  // Filter products based on selected filter
  const filteredProducts = products.filter((product) => {
    if (filter === 'all') return true;
    // Match filter to product tags (case-insensitive)
    return product.tags.some(tag => tag.toLowerCase().includes(filter.toLowerCase()));
  });

  return (
    <section style={{
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      padding: '140px clamp(20px, 5vw, 40px) 80px',
      overflowX: 'hidden',
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '60px' }}
        >
          <h1 style={{
            fontFamily: theme.fonts.display,
            fontSize: 'clamp(40px, 6vw, 64px)',
            fontWeight: '800',
            color: theme.colors.text,
            margin: '0 0 16px 0',
          }}>
            SHOP
          </h1>
          <p style={{
            fontFamily: theme.fonts.body,
            fontSize: '18px',
            color: theme.colors.textMuted,
            margin: 0,
          }}>
            {isSubdomainSite && subdomain && (
              <span style={{
                color: theme.colors.accent,
                fontWeight: '600',
                textTransform: 'uppercase',
                fontSize: '14px',
                display: 'block',
                marginBottom: '8px',
                letterSpacing: '1px',
                fontFamily: theme.fonts.mono
              }}>
                {subdomain}.cloudcarded.com
              </span>
            )}
            {filter === 'all'
              ? 'Find your perfect NFC card. Hover to interact.'
              : `Showing ${filteredProducts.length} ${filter} ${filteredProducts.length === 1 ? 'card' : 'cards'}`
            }
          </p>
        </motion.div>

        {/* Filter Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '48px',
            flexWrap: 'wrap',
          }}
        >
          {['all', 'premium', 'custom', 'team'].map((f) => (
            <motion.button
              key={f}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilter(f)}
              style={{
                padding: '10px 24px',
                background: filter === f ? theme.colors.accent : theme.colors.surface,
                border: `1px solid ${filter === f ? theme.colors.accent : theme.colors.border}`,
                borderRadius: '100px',
                cursor: 'pointer',
                fontFamily: theme.fonts.mono,
                fontSize: '12px',
                fontWeight: '500',
                color: filter === f ? theme.colors.bg : theme.colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {f}
            </motion.button>
          ))}
        </motion.div>

        {/* Product Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
              gap: 'clamp(16px, 4vw, 32px)',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} theme={theme} />
              ))
            ) : (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '60px 20px',
                color: theme.colors.textMuted,
                fontFamily: theme.fonts.body,
              }}>
                <p style={{ fontSize: '18px', margin: 0 }}>
                  No products found for "{filter}". Try a different filter.
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

// ============================================================================
// ABOUT PAGE
// ============================================================================

function AboutPage({ theme }) {
  return (
    <section style={{
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      padding: '140px clamp(20px, 5vw, 40px) 80px',
      overflowX: 'hidden',
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 style={{
            fontFamily: theme.fonts.display,
            fontSize: 'clamp(40px, 6vw, 64px)',
            fontWeight: '800',
            color: theme.colors.text,
            margin: '0 0 40px 0',
          }}>
            ABOUT{' '}
            <span style={{
              background: theme.colors.gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}>
              cloudcard
            </span>
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{
            fontFamily: theme.fonts.body,
            fontSize: '18px',
            color: theme.colors.textMuted,
            lineHeight: '1.8',
          }}
        >
          <p style={{ marginBottom: '32px' }}>
            cloudcard makes sharing your contact info faster. Each card tracks who taps it, when, and where. Built for people who want to know which connections stick.
          </p>

          <p style={{ marginBottom: '32px' }}>
            These aren't typical business cards. Each one logs unique visits, tracks engagement metrics, and shows you which connections turn into real relationships.
          </p>

          <div style={{
            background: theme.colors.surface,
            padding: '40px',
            borderRadius: '16px',
            border: `1px solid ${theme.colors.border}`,
            marginTop: '48px',
          }}>
            <h3 style={{
              fontFamily: theme.fonts.display,
              fontSize: '24px',
              fontWeight: '700',
              color: theme.colors.text,
              margin: '0 0 24px 0',
            }}>
              OUR MISSION
            </h3>
            <p style={{ margin: 0, color: theme.colors.text }}>
              Build a better tool for sharing contact info and tracking real connections.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
            gap: 'clamp(16px, 3vw, 24px)',
            marginTop: '48px',
            textAlign: 'center',
            width: '100%',
            maxWidth: '100%',
          }}>
            {[
              { value: '50K+', label: 'Cards Shipped' },
              { value: '99.9%', label: 'Tap Success Rate' },
              { value: '4.9★', label: 'Customer Rating' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + i * 0.1 }}
                style={{
                  padding: '32px',
                  background: theme.colors.surface,
                  borderRadius: '12px',
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <div style={{
                  fontFamily: theme.fonts.display,
                  fontSize: '36px',
                  fontWeight: '800',
                  background: theme.colors.gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  marginBottom: '8px',
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontFamily: theme.fonts.mono,
                  fontSize: '12px',
                  color: theme.colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}>
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>

          {/* NFC Technology Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ marginTop: '80px' }}
          >
            <h2 style={{
              fontFamily: theme.fonts.display,
              fontSize: '36px',
              fontWeight: '700',
              color: theme.colors.text,
              marginBottom: '24px',
            }}>
              How <span style={{
                background: theme.colors.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}>NFC Technology</span> Works
            </h2>

            <p style={{ marginBottom: '32px', color: theme.colors.textMuted }}>
              Near-Field Communication (NFC) is a wireless technology that enables secure data exchange between devices over very short distances (typically 4cm or less).
              It's the same proven technology that powers contactless payments like Apple Pay and Google Pay.
            </p>

            <div style={{
              background: theme.colors.surface,
              padding: '40px',
              borderRadius: '16px',
              border: `1px solid ${theme.colors.border}`,
              marginBottom: '32px',
            }}>
              <h3 style={{
                fontFamily: theme.fonts.display,
                fontSize: '24px',
                fontWeight: '700',
                color: theme.colors.text,
                marginBottom: '24px',
              }}>
                3 Simple Steps
              </h3>
              <div style={{ display: 'grid', gap: '24px' }}>
                {[
                  { step: '1', title: 'Tap Your Card', desc: 'Hold your NFC card near any smartphone (within 4cm). The phone detects the chip automatically.' },
                  { step: '2', title: 'Instant Connection', desc: 'The NFC chip transmits your digital profile wirelessly using secure near-field communication.' },
                  { step: '3', title: 'Contact Saved', desc: 'Your details appear instantly on their phone, ready to be saved. The entire process takes seconds.' },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'flex-start',
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: theme.colors.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <span style={{
                        fontFamily: theme.fonts.display,
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#ffffff',
                      }}>{item.step}</span>
                    </div>
                    <div>
                      <div style={{
                        fontFamily: theme.fonts.display,
                        fontSize: '18px',
                        fontWeight: '600',
                        color: theme.colors.text,
                        marginBottom: '4px',
                      }}>{item.title}</div>
                      <div style={{
                        fontSize: '15px',
                        color: theme.colors.textMuted,
                      }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
              gap: 'clamp(20px, 4vw, 28px)',
              marginBottom: '32px',
              width: '100%',
              maxWidth: '100%',
            }}>
              {[
                { title: 'Instant Sharing', desc: 'Share complete contact info with a single tap. No apps required.' },
                { title: 'Always Up-to-Date', desc: 'Update your info anytime. Everyone gets the latest details automatically.' },
                { title: 'Secure Transfer', desc: 'Bank-level encryption. Ultra-short range prevents unauthorized scanning.' },
                { title: 'Eco-Friendly', desc: 'One card replaces thousands of paper cards over its lifetime.' },
                { title: 'Rich Content', desc: 'Share videos, portfolio links, social media, calendars, and more.' },
                { title: 'Works Offline', desc: 'Unlike QR codes, NFC works without an internet connection.' },
              ].map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  whileHover={{
                    y: -4,
                    borderColor: theme.colors.accent,
                    boxShadow: `0 8px 20px ${theme.colors.accent}15`,
                  }}
                  style={{
                    background: theme.colors.surface,
                    padding: '24px',
                    borderRadius: '12px',
                    border: `1px solid ${theme.colors.border}`,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '4px',
                    background: theme.colors.gradient,
                    borderRadius: '2px',
                    marginBottom: '16px',
                  }} />
                  <div style={{
                    fontFamily: theme.fonts.display,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: theme.colors.text,
                    marginBottom: '8px',
                  }}>{benefit.title}</div>
                  <div style={{
                    fontSize: '14px',
                    color: theme.colors.textMuted,
                    lineHeight: '1.5',
                  }}>{benefit.desc}</div>
                </motion.div>
              ))}
            </div>

            <div style={{
              background: theme.colors.gradient,
              padding: '32px',
              borderRadius: '16px',
              textAlign: 'center',
            }}>
              <div style={{
                fontFamily: theme.fonts.display,
                fontSize: '20px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '12px',
              }}>
                The Future is Here
              </div>
              <div style={{
                fontSize: '15px',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.6',
              }}>
                By 2026, NFC business cards are becoming the norm for professional networking.
                Join thousands who've made the switch to faster, smarter contact sharing.
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// SUPPORT PAGE
// ============================================================================

function SupportPage({ showError, showSuccess, showInfo }) {
  const {
    values: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    reset
  } = useFormValidation(
    { name: '', email: '', subject: '', message: '' },
    {
      name: validators.required,
      email: validators.email,
      subject: validators.required,
      message: validators.required,
    }
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    if (!validateAll()) {
      showError('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      // Serverless form handler would go here
      // e.g., await fetch('/api/contact', { method: 'POST', body: JSON.stringify(formData) })

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log('Form submitted:', formData);
      showSuccess('Message sent! We\'ll get back to you soon.');

      // Reset form
      reset();
    } catch (error) {
      showError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section style={{
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      padding: '140px clamp(20px, 5vw, 40px) 80px',
      overflowX: 'hidden',
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 style={{
            fontFamily: theme.fonts.display,
            fontSize: 'clamp(40px, 6vw, 64px)',
            fontWeight: '800',
            color: theme.colors.text,
            margin: '0 0 16px 0',
          }}>
            SUPPORT
          </h1>
          <p style={{
            fontFamily: theme.fonts.body,
            fontSize: '18px',
            color: theme.colors.textMuted,
            margin: '0 0 48px 0',
          }}>
            Questions? We're here to help. Reach out anytime.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          onSubmit={handleSubmit}
          style={{
            background: theme.colors.surface,
            padding: '48px',
            borderRadius: '16px',
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <div style={{ display: 'grid', gap: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <label htmlFor="support-name" style={{
                  display: 'block',
                  fontFamily: theme.fonts.mono,
                  fontSize: '12px',
                  color: theme.colors.textMuted,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}>
                  Name
                </label>
                <input
                  id="support-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: theme.colors.bgAlt,
                    border: `1px solid ${touched.name && errors.name ? '#ef4444' : theme.colors.border}`,
                    borderRadius: '8px',
                    fontFamily: theme.fonts.body,
                    fontSize: '16px',
                    color: theme.colors.text,
                    outline: 'none',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = touched.name && errors.name ? '#ef4444' : theme.colors.accent;
                    e.target.style.boxShadow = touched.name && errors.name ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(14, 165, 233, 0.1)';
                  }}
                  onBlur={(e) => {
                    handleBlur('name');
                    e.target.style.borderColor = touched.name && errors.name ? '#ef4444' : theme.colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {touched.name && errors.name && (
                  <div style={{
                    color: '#ef4444',
                    fontSize: '14px',
                    marginTop: '4px',
                    fontFamily: theme.fonts.body,
                  }}>
                    {errors.name}
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="support-email" style={{
                  display: 'block',
                  fontFamily: theme.fonts.mono,
                  fontSize: '12px',
                  color: theme.colors.textMuted,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}>
                  Email
                </label>
                <input
                  id="support-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: theme.colors.bgAlt,
                    border: `1px solid ${touched.email && errors.email ? '#ef4444' : theme.colors.border}`,
                    borderRadius: '8px',
                    fontFamily: theme.fonts.body,
                    fontSize: '16px',
                    color: theme.colors.text,
                    outline: 'none',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = touched.email && errors.email ? '#ef4444' : theme.colors.accent;
                    e.target.style.boxShadow = touched.email && errors.email ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(14, 165, 233, 0.1)';
                  }}
                  onBlur={(e) => {
                    handleBlur('email');
                    e.target.style.borderColor = touched.email && errors.email ? '#ef4444' : theme.colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {touched.email && errors.email && (
                  <div style={{
                    color: '#ef4444',
                    fontSize: '14px',
                    marginTop: '4px',
                    fontFamily: theme.fonts.body,
                  }}>
                    {errors.email}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="support-subject" style={{
                display: 'block',
                fontFamily: theme.fonts.mono,
                fontSize: '12px',
                color: theme.colors.textMuted,
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}>
                Subject
              </label>
              <select
                id="support-subject"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: theme.colors.bgAlt,
                  border: `1px solid ${touched.subject && errors.subject ? '#ef4444' : theme.colors.border}`,
                  borderRadius: '8px',
                  fontFamily: theme.fonts.body,
                  fontSize: '16px',
                  color: theme.colors.text,
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = touched.subject && errors.subject ? '#ef4444' : theme.colors.accent;
                  e.target.style.boxShadow = touched.subject && errors.subject ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(14, 165, 233, 0.1)';
                }}
                onBlur={(e) => {
                  handleBlur('subject');
                  e.target.style.borderColor = touched.subject && errors.subject ? '#ef4444' : theme.colors.border;
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select a topic...</option>
                <option value="order">Order Issue</option>
                <option value="product">Product Question</option>
                <option value="custom">Custom Design</option>
                <option value="other">Other</option>
              </select>
              {touched.subject && errors.subject && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '14px',
                  marginTop: '4px',
                  fontFamily: theme.fonts.body,
                }}>
                  {errors.subject}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="support-message" style={{
                display: 'block',
                fontFamily: theme.fonts.mono,
                fontSize: '12px',
                color: theme.colors.textMuted,
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}>
                Message
              </label>
              <textarea
                id="support-message"
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={5}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: theme.colors.bgAlt,
                  border: `1px solid ${touched.message && errors.message ? '#ef4444' : theme.colors.border}`,
                  borderRadius: '8px',
                  fontFamily: theme.fonts.body,
                  fontSize: '16px',
                  color: theme.colors.text,
                  outline: 'none',
                  resize: 'vertical',
                  transition: 'all 0.2s ease',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = touched.message && errors.message ? '#ef4444' : theme.colors.accent;
                  e.target.style.boxShadow = touched.message && errors.message ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(14, 165, 233, 0.1)';
                }}
                onBlur={(e) => {
                  handleBlur('message');
                  e.target.style.borderColor = touched.message && errors.message ? '#ef4444' : theme.colors.border;
                  e.target.style.boxShadow = 'none';
                }}
              />
              {touched.message && errors.message && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '14px',
                  marginTop: '4px',
                  fontFamily: theme.fonts.body,
                }}>
                  {errors.message}
                </div>
              )}
            </div>

            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={!isSubmitting ? { scale: 1.02 } : {}}
              whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              style={{
                padding: '16px 32px',
                background: isSubmitting ? theme.colors.surfaceHover : theme.colors.gradient,
                border: 'none',
                borderRadius: '10px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontFamily: theme.fonts.display,
                fontSize: '16px',
                fontWeight: '600',
                color: theme.colors.bg,
                marginTop: '8px',
                opacity: isSubmitting ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
              }}
            >
              {isSubmitting && (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: theme.colors.bg,
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite',
                }} />
              )}
              {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
            </motion.button>
          </div>
        </motion.form>

        {/* FAQ Preview */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          style={{ marginTop: '48px' }}
        >
          <h3 style={{
            fontFamily: theme.fonts.display,
            fontSize: '24px',
            fontWeight: '700',
            color: theme.colors.text,
            margin: '0 0 24px 0',
          }}>
            QUICK ANSWERS
          </h3>
          
          {[
            { q: 'How long does shipping take?', a: '3-5 business days for standard, 1-2 for express.' },
            { q: 'Can I customize my card design?', a: 'Yes! FRND Custom allows full design customization.' },
            { q: 'What devices are compatible?', a: 'All NFC-enabled iOS (iPhone 7+) and Android devices.' },
          ].map((faq, i) => (
            <div
              key={i}
              style={{
                padding: '20px 24px',
                background: theme.colors.surface,
                borderRadius: '12px',
                border: `1px solid ${theme.colors.border}`,
                marginBottom: '12px',
              }}
            >
              <div style={{
                fontFamily: theme.fonts.display,
                fontSize: '16px',
                fontWeight: '600',
                color: theme.colors.text,
                marginBottom: '8px',
              }}>
                {faq.q}
              </div>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                color: theme.colors.textMuted,
              }}>
                {faq.a}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// FOOTER
// ============================================================================

function Footer({ theme }) {
  return (
    <footer style={{
      padding: '60px 40px',
      borderTop: `1px solid ${theme.colors.border}`,
      background: theme.colors.bgAlt,
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '24px',
      }}>
        <div style={{
          fontFamily: theme.fonts.display,
          fontSize: '20px',
          fontWeight: '800',
          background: theme.colors.gradient,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          cloudcard
        </div>
        
        <div style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textMuted,
        }}>
          © 2025 cloudcard. All rights reserved.
        </div>
        
        <div style={{ display: 'flex', gap: '16px' }}>
          {[
            { name: 'Twitter', url: 'https://twitter.com/cloudcard' },
            { name: 'Instagram', url: 'https://instagram.com/cloudcard' },
            { name: 'Discord', url: 'https://discord.gg/cloudcard' }
          ].map((social) => (
            <motion.a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ color: theme.colors.accent }}
              style={{
                fontFamily: theme.fonts.mono,
                fontSize: '12px',
                color: theme.colors.textMuted,
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {social.name}
            </motion.a>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ============================================================================
// MAIN APP
// ============================================================================

export default function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const { toast, showError, showSuccess, showInfo, hideToast } = useToast();
  const { subdomain } = useSubdomain();
  const activeTheme = getTheme(subdomain);

  // Global keyboard shortcuts
  useKeyboardShortcuts({
    onEscape: () => {
      if (currentPage !== 'home') {
        setCurrentPage('home');
      }
    },
  });

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage setCurrentPage={setCurrentPage} theme={activeTheme} />;
      case 'shop':
        return <ShopPage theme={activeTheme} />;
      case 'design':
        return <CardDesignerV2 showError={showError} showSuccess={showSuccess} showInfo={showInfo} />;
      case 'cart':
        return <SimpleCart setCurrentPage={setCurrentPage} showError={showError} showSuccess={showSuccess} />;
      case 'about':
        return <AboutPage theme={activeTheme} />;
      case 'support':
        return <SupportPage showError={showError} showSuccess={showSuccess} showInfo={showInfo} />;
      default:
        return <HomePage setCurrentPage={setCurrentPage} theme={activeTheme} />;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: activeTheme.colors.bg,
      color: activeTheme.colors.text,
    }}>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Syne:wght@600;700;800&display=swap"
        rel="stylesheet"
      />

      {/* Global Styles */}
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
          overflow-x: hidden;
          width: 100%;
        }

        body {
          background: ${activeTheme.colors.bg};
          overflow-x: hidden;
          width: 100%;
          position: relative;
        }

        #root {
          overflow-x: hidden;
          width: 100%;
        }

        /* Prevent horizontal scroll */
        * {
          max-width: 100%;
        }

        /* Ensure canvas elements don't cause overflow */
        canvas {
          max-width: 100vw !important;
          touch-action: pan-y pinch-zoom;
        }

        h1, h2, h3, h4, h5, h6, p {
          overflow-wrap: break-word;
          word-wrap: break-word;
          hyphens: auto;
          max-width: 100%;
        }

        ::selection {
          background: ${activeTheme.colors.accent};
          color: ${activeTheme.colors.bg};
        }

        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: ${activeTheme.colors.bg};
        }

        ::-webkit-scrollbar-thumb {
          background: ${activeTheme.colors.surface};
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${activeTheme.colors.surfaceHover};
        }

        input, textarea, select {
          font-family: inherit;
        }

        input::placeholder, textarea::placeholder {
          color: ${activeTheme.colors.textMuted};
        }

        /* Loading spinner animation */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} theme={activeTheme} />
      
      <AnimatePresence mode="wait">
        <motion.main
          key={currentPage}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderPage()}
        </motion.main>
      </AnimatePresence>

      <Footer theme={activeTheme} />

      {/* Toast notifications */}
      <Toast
        message={toast?.message}
        type={toast?.type}
        onClose={hideToast}
      />
    </div>
  );
}
